import { useState } from 'react';
import type { DefinitionProcess, JsonObject, Problem, StudioTrial } from '../api/types';
import { Button } from '../components/Button';
import { ProblemNotice } from '../components/ProblemNotice';
import { Spinner } from '../components/Spinner';
import { Link } from '../app/Link';
import type { Route } from '../app/router';
import { deployPhaseView, trialProblemText, trialStatusView } from '../app/status';

export interface RunStripProps {
  workspaceId: string;
  appId: string;
  trial: StudioTrial;
  /**
   * いまの head の digest（DraftReview.filesDigest）。trial.filesDigest と違えば前の版の帯として扱う。
   * まだレビューを読んでいなければ null（判定しない）。
   */
  headDigest: string | null;
  /** 配備後の getDefinition のプロセス。空なら開始フォームを出さない。 */
  processes: DefinitionProcess[];
  /** getDefinition の読み込み中。読み込み中と「取得できなかった」を同じ表示にしない（A11）。 */
  processesLoading?: boolean;
  starting: boolean;
  problem: Problem | null;
  onStart(processKey: string, input: JsonObject): void;
  onDismiss(): void;
  /** 読み直しの失敗に添える「再取得」（渡さなければ出さない。A4）。 */
  onReload?(): void;
  /** 「AI に原因を聞く」（Studio だけ。無ければボタンを出さない）。 */
  onAskFix?(text: string): void;
  navigate(route: Route): void;
}

/**
 * 試運転の帯（Claude Design の run strip。計画の S5 G11）。
 * 配備（4 工程）と実行（run）を 1 本の進捗に畳まず、止まった工程と理由をそのまま出す。
 * 「試運転を止める」は作らない（run のキャンセル API が無い。G18）。
 */
export function RunStrip({
  workspaceId,
  appId,
  trial,
  headDigest,
  processes,
  processesLoading = false,
  starting,
  problem,
  onStart,
  onDismiss,
  onAskFix,
  onReload,
  navigate,
}: RunStripProps) {
  const view = trialStatusView(trial.status);
  const reason = trialProblemText(trial);
  const done = trial.phases.filter((phase) => phase.mark === 'ok').length;
  // 下書きが進むと DraftReview.trial は null に戻るが、手元の帯は残る（useDraftTrial は初期値を下げない）。
  // digest が食い違う帯は前の版のものなので、開始フォームを出さず「閉じる」で片付けられるようにする。
  const stale = headDigest !== null && headDigest !== trial.filesDigest;

  return (
    <section className="studio-run-strip" aria-label="試運転">
      {/* section に role を付けると暗黙の region が上書きされ、テストが region で引けなくなる（骨組み 6.11）。
          読み上げの更新が要るのは状態の見出しと補足だけなので、role="status" は内側に付ける。 */}
      <div className="studio-run-strip-head" role="status">
        <strong className="studio-run-strip-title">{view.label}</strong>
        {trial.status === 'deploying' && (
          <span className="studio-run-strip-note">
            {/* engine への配備の結果が分かるまでは工程の数ではなく待っている理由を出す（BR-1）。 */}
            {trial.upload.engineStatus === 'unknown'
              ? '配備中 · engine の応答を確認しています'
              : `配備中 · ${done} / ${trial.phases.length} 工程`}
          </span>
        )}
        {trial.status === 'deploy_failed' && <span className="studio-run-strip-note">試運転を始められませんでした</span>}
        {trial.status === 'ready' && !stale && (
          <span className="studio-run-strip-note">配備が終わりました · プロセスを選んで試運転を始めます</span>
        )}
        {trial.status === 'running' && (
          <span className="studio-run-strip-note">
            {`試運転中 · ${trial.awaiting.length > 0 ? '自動処理が終わり、担当者の確認を待っています' : '実行中です'}`}
          </span>
        )}
        {isSettled(trial) && <span className="studio-run-strip-note">{`${view.label} · ${outcomeText(trial, reason)}`}</span>}
      </div>

      {(trial.status === 'deploying' || trial.status === 'deploy_failed') && (
        <ul className="studio-run-strip-phases">
          {trial.phases.map((phase) => {
            const mark = deployPhaseView(phase.mark);
            return (
              <li key={phase.code} className={`studio-run-strip-phase studio-run-strip-phase-${phase.mark}`}>
                <span aria-hidden="true">{mark.symbol}</span>
                {phase.label}
              </li>
            );
          })}
        </ul>
      )}

      {trial.status === 'deploy_failed' && (
        <div className="studio-run-strip-actions">
          <span className="failure">{`理由：${reason ?? '理由が取得できませんでした'}`}</span>
          {onAskFix !== undefined && (
            <Button onClick={() => onAskFix(`試運転が失敗した原因を調べて直してください: ${reason ?? trial.upload.status}`)}>
              AI に原因を聞く
            </Button>
          )}
        </div>
      )}

      {(trial.status === 'needs_attention' || trial.status === 'failed') && (
        <div className="studio-run-strip-actions">
          <p className="failure">{reason ?? 'Taskのエラーを確認してください。'}</p>
          <ul>
            {(trial.failures ?? []).map((failure) => (
              <li key={failure.requestId}>
                {trial.runId !== null && <Link
                  to={{ name: 'run', workspaceId, appId, runId: trial.runId, element: failure.elementId }}
                  onNavigate={navigate}
                >{failure.name}のエラーを見る</Link>}
                <span>{failure.error?.detail}</span>
                {failure.status === 'outcome_unknown' && <p>処理が実行されたか不明です。再実行前に外部の処理結果を確認してください。</p>}
              </li>
            ))}
          </ul>
          {onAskFix && <Button onClick={() => onAskFix(
            `試運転 ${trial.id}（runId: ${trial.runId}、revisionId: ${trial.revisionId}）のエラーを調べて直してください。` +
            `trial_getで失敗Taskを確認してください。対象: ${(trial.failures ?? []).map((item) => item.elementId).join(', ')}。` +
            '修正後は検証・再配備して新しい試運転を行います。結果不明の副作用は再実行前に確認してください。'
          )}>AI に原因を聞く</Button>}
          <p className="hint">修正後に「試す」で再配備してください。前回の開始入力を使って新しい試運転を始められます。実行済みの更新や送信は残るため、開始前に確認してください。</p>
        </div>
      )}

      {stale && <p className="hint">下書きが変わりました。もう一度「試す」で配備してください。</p>}

      {trial.status === 'ready' &&
        !stale &&
        (processesLoading ? (
          <Spinner label="配備した定義を取得しています" />
        ) : processes.length === 0 ? (
          <p className="hint">配備した定義のプロセスを取得できませんでした。</p>
        ) : (
          <StartTrialForm key={trial.id} processes={processes} starting={starting} onStart={onStart} restartFrom={trial.restartFrom} />
        ))}

      {(trial.status === 'running' || trial.status === 'needs_attention') && trial.awaiting.length > 0 && trial.runId !== null && (
        <ul className="studio-run-strip-awaiting">
          {trial.awaiting.map((task) => (
            <li key={task.requestId}>
              {task.name}{' '}
              <Link
                to={{
                  name: 'run',
                  workspaceId,
                  appId,
                  runId: trial.runId ?? '',
                  element: task.elementId,
                  panel: 'answer',
                }}
                onNavigate={navigate}
              >
                確認フォームを開く
              </Link>
            </li>
          ))}
        </ul>
      )}

      {/* 配備に失敗した帯も片付けられるようにする（次の配備まで残さない。A12）。 */}
      {(isSettled(trial) || stale || trial.status === 'needs_attention' || trial.status === 'deploy_failed') && (
        <div className="studio-run-strip-actions">
          {trial.runId !== null && (
            <Link to={{ name: 'run', workspaceId, appId, runId: trial.runId }} onNavigate={navigate}>
              実行履歴で結果を見る →
            </Link>
          )}
          <Button onClick={onDismiss}>閉じる</Button>
        </div>
      )}

      <ProblemNotice
        problem={problem}
        tone={problem !== null && problem.status === 503 ? 'notice' : 'error'}
        {...(onReload ? { action: <Button onClick={onReload}>再取得</Button> } : {})}
      />
    </section>
  );
}

function isSettled(trial: StudioTrial): boolean {
  return trial.status === 'completed' || trial.status === 'failed' || trial.status === 'cancelled';
}

/** 終わった試運転の一言。completed は endEventId でしか業務上の意味が分からない（status.ts と同じ約束）。 */
function outcomeText(trial: StudioTrial, reason: string | null): string {
  if (trial.status === 'completed') {
    const endEventId = trial.run?.endEventId ?? null;
    return endEventId === null ? '終了（終了イベント不明）' : `終了イベント ${endEventId}`;
  }
  return reason ?? '理由は実行履歴で確認してください';
}

interface StartTrialFormProps {
  restartFrom: StudioTrial['restartFrom'];
  processes: DefinitionProcess[];
  starting: boolean;
  onStart(processKey: string, input: JsonObject): void;
}

/**
 * 開始入力は JSON object で受ける。RunStripProps には inputSchema を渡す口が無いので、
 * StartRunPanel のようなフォームの生成はしない（骨組みへの確認事項）。
 */
function StartTrialForm({ processes, starting, onStart, restartFrom }: StartTrialFormProps) {
  const [processKey, setProcessKey] = useState(processes[0]?.key ?? '');
  const [body, setBody] = useState('{}');
  const parsed = parseJsonObject(body);

  return (
    <div className="studio-run-strip-form">
      {restartFrom && processes.some((process) => process.key === restartFrom.processKey) && (
        <div>
          <Button onClick={() => {
            setProcessKey(restartFrom.processKey);
            setBody(JSON.stringify(restartFrom.input, null, 2));
          }}>前回の開始入力を使う</Button>
          <p className="hint">新しい実行を最初から開始します。前回の更新・送信は残ります。入力と処理結果を確認してください。</p>
        </div>
      )}
      {processes.length > 1 && (
        <label className="field inline">
          <span className="field-label">プロセス</span>
          <select className="input" aria-label="プロセス" value={processKey} onChange={(event) => setProcessKey(event.target.value)}>
            {processes.map((process) => (
              <option key={process.key} value={process.key}>
                {process.key}
              </option>
            ))}
          </select>
        </label>
      )}
      <label className="field">
        <span className="field-label">開始入力（JSON object）</span>
        <textarea
          className="input mono"
          aria-label="開始入力（JSON object）"
          rows={3}
          value={body}
          onChange={(event) => setBody(event.target.value)}
        />
        {!parsed.ok && <span className="field-error">{parsed.message}</span>}
      </label>
      <Button
        variant="primary"
        disabled={starting || !parsed.ok || processKey === ''}
        onClick={() => {
          if (!parsed.ok) return;
          onStart(processKey, parsed.value);
        }}
      >
        {starting ? '開始中…' : '試運転を始める'}
      </Button>
    </div>
  );
}

type ParsedBody = { ok: true; value: JsonObject } | { ok: false; message: string };

function parseJsonObject(text: string): ParsedBody {
  try {
    const value: unknown = JSON.parse(text);
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      return { ok: false, message: 'JSON objectを入力してください。' };
    }
    return { ok: true, value: value as JsonObject };
  } catch {
    return { ok: false, message: 'JSONとして読めません。' };
  }
}
